import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { Toaster } from "@/components/ui/sonner";
import SiteLayout from "@/components/site/SiteLayout";
import Home from "@/pages/Home";
import Cars from "@/pages/Cars";
import CarDetail from "@/pages/CarDetail";
import Checkout from "@/pages/Checkout";
import PaymentResult from "@/pages/PaymentResult";
import MyBookings from "@/pages/MyBookings";
import { About, Contact, Faq } from "@/pages/InfoPages";
import { LegalIndex, LegalPage } from "@/pages/Legal";
import { SignInPage, SignUpPage, AdminSignInPage } from "@/pages/AuthPages";
import OfficerConsole from "@/pages/OfficerConsole";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCars from "@/pages/admin/AdminCars";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminKyc from "@/pages/admin/AdminKyc";
import AdminFleet from "@/pages/admin/AdminFleet";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import { HostPage, BusinessPage } from "@/pages/PartnerPages";
import HostDashboard from "@/pages/HostDashboard";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import Verify from "@/pages/Verify";
import Invoice from "@/pages/Invoice";
import AppPay, { AppPayDone, AppPhone, EmailVerified } from "@/pages/AppPay";
import { WishlistPage, ComparePage, OffersPage, NotFound } from "@/pages/CustomerPages";
import Support from "@/pages/Support";
import { AdminReports, AdminTickets, AdminAudit, AdminIntegrations } from "@/pages/admin/AdminOps";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function ReferralCapture() {
  const { search } = useLocation();
  useEffect(() => {
    const ref = new URLSearchParams(search).get("ref");
    if (ref && /^[A-Za-z0-9]{4,20}$/.test(ref)) localStorage.setItem("hc_ref", ref.toUpperCase());
  }, [search]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ClerkAuthProvider>
          <ScrollTop />
          <ReferralCapture />
          <Toaster position="top-center" richColors />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/cars" element={<Cars />} />
              <Route path="/cars/:id" element={<CarDetail />} />
              <Route path="/checkout/:carId" element={<Checkout />} />
              <Route path="/payment-result" element={<PaymentResult />} />
              <Route path="/app-pay" element={<AppPay />} />
              <Route path="/app-pay/done" element={<AppPayDone />} />
              <Route path="/bookings" element={<MyBookings />} />
              <Route path="/verify" element={<Verify />} />
              <Route path="/invoice/:id" element={<Invoice />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/host" element={<HostPage />} />
              <Route path="/host/dashboard" element={<HostDashboard />} />
              <Route path="/business" element={<BusinessPage />} />
              <Route path="/my-bookings" element={<Navigate to="/bookings" replace />} />
              <Route path="/wishlist" element={<WishlistPage />} />
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/offers" element={<OffersPage />} />
              <Route path="/support" element={<Support />} />
              <Route path="*" element={<NotFound />} />
              <Route path="/legal" element={<LegalIndex />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/sign-in/*" element={<SignInPage />} />
              <Route path="/sign-up/*" element={<SignUpPage />} />
            </Route>
            <Route path="/app-phone" element={<div className="min-h-screen bg-hc-cream"><AppPhone /></div>} />
            <Route path="/email-verified" element={<div className="min-h-screen bg-hc-cream"><EmailVerified /></div>} />
            <Route path="/admin/sign-in/*" element={<AdminSignInPage />} />
            <Route path="/officer/sign-in" element={<Navigate to="/officer" replace />} />
            <Route path="/officer/*" element={<OfficerConsole />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="cars" element={<AdminCars />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="verifications" element={<AdminKyc />} />
              <Route path="fleet" element={<AdminFleet />} />
              <Route path="pricing" element={<AdminPricing />} />
              <Route path="enquiries" element={<AdminEnquiries />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="tickets" element={<AdminTickets />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="integrations" element={<AdminIntegrations />} />
            </Route>
          </Routes>
        </ClerkAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
